import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, Loader2, SearchX, Plus } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORIES, categoryById } from "../lib/data";
import ListingCard from "./ListingCard";
import FilterSidebar from "./FilterSidebar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const emptyFilters = { category: "", subcategory: [], city: "", area: "", minPrice: "", maxPrice: "", sellerType: "", sort: "newest", q: "" };

export default function ListingsBrowser({ lockedCategory = "", showQuery = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    ...emptyFilters,
    category: lockedCategory || searchParams.get("category") || "",
    city: searchParams.get("city") || "",
    area: searchParams.get("area") || "",
    q: searchParams.get("q") || "",
  }));
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      category: lockedCategory || searchParams.get("category") || "",
      city: searchParams.get("city") || f.city,
      area: searchParams.get("area") || f.area,
      q: searchParams.get("q") || "",
    }));
    setPage(1);
    // eslint-disable-next-line
  }, [lockedCategory, searchParams.get("q"), searchParams.get("category"), searchParams.get("city"), searchParams.get("area")]);

  const fetchListings = useCallback(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const params = { page, limit: 12, sort: filters.sort };
    if (filters.category) params.category = filters.category;
    if (filters.subcategory && filters.subcategory.length) params.subcategory = filters.subcategory.join(",");
    if (filters.city) params.city = filters.city;
    if (filters.area) params.area = filters.area;
    if (filters.sellerType) params.sellerType = filters.sellerType;
    if (filters.minPrice) params.minPrice = filters.minPrice;
    if (filters.maxPrice) params.maxPrice = filters.maxPrice;
    if (filters.q) params.q = filters.q;
    api.get("/listings", { params })
      .then((r) => { if (reqId === reqIdRef.current) setData(r.data); })
      .catch(() => { if (reqId === reqIdRef.current) setData({ items: [], total: 0, pages: 0 }); })
      .finally(() => { if (reqId === reqIdRef.current) setLoading(false); });
  }, [filters, page]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const onChange = (next) => { setFilters(next); setPage(1); };
  const onReset = () => { setFilters({ ...emptyFilters, category: lockedCategory, q: filters.q }); setPage(1); };

  const activeTags = useMemo(() => {
    const tags = [];
    if (!lockedCategory && filters.category) tags.push({ key: "category", label: categoryById(filters.category)?.label || filters.category });
    (filters.subcategory || []).forEach((s) => tags.push({ key: `sub:${s}`, label: s }));
    if (filters.city) tags.push({ key: "city", label: filters.city });
    if (filters.area) tags.push({ key: "area", label: filters.area });
    if (filters.sellerType) tags.push({ key: "sellerType", label: filters.sellerType });
    if (filters.minPrice || filters.maxPrice) tags.push({ key: "price", label: `₹${filters.minPrice || 0} - ${filters.maxPrice || "∞"}` });
    return tags;
  }, [filters, lockedCategory]);

  const removeTag = (key) => {
    if (key === "category") onChange({ ...filters, category: "", subcategory: [] });
    else if (key.startsWith("sub:")) onChange({ ...filters, subcategory: filters.subcategory.filter((s) => s !== key.slice(4)) });
    else if (key === "city") onChange({ ...filters, city: "", area: "" });
    else if (key === "area") onChange({ ...filters, area: "" });
    else if (key === "sellerType") onChange({ ...filters, sellerType: "" });
    else if (key === "price") onChange({ ...filters, minPrice: "", maxPrice: "" });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block">
          <div className="bg-card rounded-card shadow-soft border border-border p-5 sticky top-20">
            <FilterSidebar filters={filters} onChange={onChange} onReset={onReset} />
          </div>
        </aside>

        <div>
          {/* Results header */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-ink font-medium" data-testid="results-count">
              {loading ? "Loading…" : `Showing ${data.total} listing${data.total === 1 ? "" : "s"}`}
            </p>
            <div className="lg:hidden">
              <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="rounded-lg gap-1.5" data-testid="open-filters-btn"><SlidersHorizontal className="h-4 w-4" /> Filters</Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
                  <div className="pt-4"><FilterSidebar filters={filters} onChange={onChange} onReset={onReset} /></div>
                  <Button className="w-full mt-6 bg-primary text-white rounded-lg" onClick={() => setDrawerOpen(false)}>Show {data.total} Results</Button>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {activeTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {activeTags.map((t) => (
                <button key={t.key} onClick={() => removeTag(t.key)} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full hover:bg-primary/20 transition-colors" data-testid={`filter-tag-${t.key}`}>
                  {t.label} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : data.items.length === 0 ? (
            <div className="py-20 text-center" data-testid="no-results">
              <SearchX className="h-12 w-12 text-muted-foreground mx-auto" />
              <h3 className="mt-4 font-display font-semibold text-lg text-ink">No listings yet. Be the first to post!</h3>
              <p className="text-muted-foreground text-sm mt-1">Be the first to list here, or adjust your filters to explore more.</p>
              <Button asChild className="mt-5 bg-accent hover:bg-accent/90 text-white rounded-lg gap-1.5"><Link to="/dashboard/create-listing"><Plus className="h-4 w-4" /> Post Free Listing</Link></Button>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {CATEGORIES.slice(0, 4).map((c) => (
                  <button key={c.id} onClick={() => onChange({ ...emptyFilters, category: lockedCategory || c.id })} className="text-xs font-medium border border-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">{c.label}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="listings-grid">
              {data.items.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
              <Button variant="outline" className="rounded-lg" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev">Prev</Button>
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} className={`h-9 w-9 rounded-lg text-sm font-medium transition-colors ${p === page ? "bg-primary text-white" : "border border-border hover:border-primary"}`} data-testid={`page-${p}`}>{p}</button>
              ))}
              <Button variant="outline" className="rounded-lg" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">Next</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
